import { useTracking } from 'react-tracking';

import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import {
  NarrativeAuthorAvatar,
  NarrativeAuthorBase,
} from '@/components/NarrativeAuthor';
import { NarrativesEmptyPanel } from '../../../NarrativesEmptyPanel';
import { useCommunityUser } from '../../hooks/useCommunityUser';
import { NarrativeInvalidationBoundary } from '../../../NarrativeInvalidationBoundary';

type Props = {
  displayName: string;
};

export const Profile = ({ displayName }: Props) => {
  const { trackEvent } = useTracking();
  const { data } = useCommunityUser({ displayName });
  const {
    narratives,
    displayName: authorDisplayName,
    imageUrl,
    name,
    role,
    url,
  } = data?.communityUser || {};

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    const { id, company } = narrative;
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: id,
      companyId: company?.id,
      uniqueSymbol: company?.tickerSymbol,
    });
  };

  return (
    <div className="relative grid w-full max-w-screen-xl p-x2 lg:p-x3">
      <div className="mb-6">
        <NarrativeAuthorBase
          title={displayName || name}
          subtitle={role !== '' ? role : 'Community Contributor'}
          url={url}
        >
          <NarrativeAuthorAvatar
            size="lg"
            author={{
              displayName: authorDisplayName,
              imageUrl,
              name,
            }}
          />
        </NarrativeAuthorBase>
      </div>

      {narratives.length ? (
        <div
          role="list"
          className="grid w-full grid-cols-[repeat(1,1fr)] gap-x1 sm:grid-cols-[repeat(3,1fr)]"
        >
          <NarrativeInvalidationBoundary
            onSetPrimary={({ invalidatePriority }) =>
              invalidatePriority(['narrative-user-list'])
            }
          >
            {narratives.map((narrative) => (
              <div role="listitem" key={narrative.id}>
                <NarrativeCardPopulated
                  showCompanyIcon
                  showAuthor={false}
                  narrative={narrative}
                  onClick={() => handleOnClick(narrative)}
                />
              </div>
            ))}
          </NarrativeInvalidationBoundary>
        </div>
      ) : (
        <NarrativesEmptyPanel>
          There are no narratives available from this user.
        </NarrativesEmptyPanel>
      )}
    </div>
  );
};
